import React, { useEffect } from 'react';
import { Markup } from 'interweave';
import Modal, { ModalTitle, ModalBody, ModalActions } from '@strava/ui/Modal';
import Button from '@strava/ui/Button';
import {
  getDataSharingOptOut,
  updateAthleteSettings
} from '@strava/api/microfrontends/athlete';
import {
  useStravaAnalytics,
  useStravaErrorLogging
} from '@strava/container-context';
import { useSafeState } from '@strava/react-hooks/src/use-safe-state';
import { ATHLETES_VISIBILITY } from '@strava/constants/src/appConstants';
import { isNil } from 'lodash-es';

import styles from './CPRAComplianceModal.module.scss';

interface Props {
  onDismiss: () => void;
  isLoggedIn: boolean;
  t: (
    key: string,
    params?: Record<string, string | number | undefined | null>
  ) => string;
  dataSharingOptedOut?: boolean;
}

const CPRAComplianceModal = ({
  onDismiss,
  isLoggedIn = false,
  t,
  dataSharingOptedOut
}: Props) => {
  const { logError } = useStravaErrorLogging();
  const { track } = useStravaAnalytics();
  const [isProcessing, setIsProcessing] = useSafeState(false);
  const [isOptedOut, setIsOptedOut] = useSafeState(
    dataSharingOptedOut || false
  );

  useEffect(() => {
    // retrieve data sharing opt out status if its not provided as a prop
    if (isNil(dataSharingOptedOut)) {
      setIsProcessing(true);
      (async () => {
        const response = await getDataSharingOptOut({ logError });
        if (response?.data?.['opted_out']) {
          setIsOptedOut(true);
        } else {
          setIsProcessing(false);
        }
      })();
    }
  }, [logError, setIsProcessing, setIsOptedOut, dataSharingOptedOut]);

  useEffect(() => {
    track({
      category: 'privacy_compliance',
      page: 'do_not_share_info',
      action: 'screen_enter',
      element: 'modal',
      properties: {
        page_url: window.location.href
      }
    });
  }, [track]);

  const handleOptOut = async () => {
    setIsProcessing(true);
    if (isLoggedIn) {
      try {
        await updateAthleteSettings({
          settings: { data_sharing: ATHLETES_VISIBILITY.optedOut },
          logError
        });
        setIsOptedOut(true);
      } catch (error) {
        // let user try again
        setIsProcessing(false);
      }
    } else {
      // domain and cookie expiration logic copied from the cookie banner logic in active
      let domain;
      if (/localhost/.test(window.location.hostname)) {
        domain = 'localhost';
      } else if (/staging\./.test(window.location.hostname)) {
        domain = '.staging.strava.com';
      } else {
        domain = '.strava.com';
      }

      const cookieExpiration = new Date();
      cookieExpiration.setFullYear(cookieExpiration.getFullYear() + 1);

      document.cookie =
        `_strava_cpra=${ATHLETES_VISIBILITY.optedOut};` +
        `expires=${cookieExpiration.toUTCString()};` +
        `domain=${domain};` +
        'path=/;';

      setIsOptedOut(true);
    }

    track({
      category: 'privacy_compliance',
      page: 'do_not_share_info',
      action: 'click',
      element: 'opt_out'
    });
  };

  return (
    <Modal
      isOpen={true}
      onDismiss={onDismiss}
      aria-label={t('modal.title')}
      className={styles.modal}
      hasClose={true}
    >
      <ModalTitle className={styles.modalTitle}>{t('modal.title')}</ModalTitle>
      <ModalBody className={styles.modalBody}>
        <Markup tagName="p" content={t('modal.body_1')} />
        <Markup tagName="p" content={t('modal.body_2_html')} />
      </ModalBody>
      <ModalActions className={styles.modalActions}>
        <Button type="button" onClick={onDismiss} variant="primaryOutline">
          {t('modal.go_back')}
        </Button>
        <Button
          type="button"
          onClick={handleOptOut}
          variant="primary"
          disabled={isProcessing || isOptedOut}
          data-cy="cpra-opt-out-btn"
        >
          {isOptedOut ? t('modal.opted_out') : t('modal.opt_out')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default CPRAComplianceModal;
